import { useEffect, useState } from "react";
import { Smartphone, X, Share, Plus, Download, RefreshCw } from "lucide-react";
import { Button } from "./ui/button";
import {
  isInstallAvailable,
  isStandalone,
  isIOS,
  promptInstall,
  promptUpdate,
} from "../lib/pwa";

const DISMISS_KEY = "ccm-install-dismissed-at";
const DISMISS_DAYS = 7;

function dismissedRecently() {
  const v = Number(localStorage.getItem(DISMISS_KEY) || 0);
  if (!v) return false;
  return Date.now() - v < DISMISS_DAYS * 24 * 60 * 60 * 1000;
}

/**
 * Floating bottom banner that prompts the user to install the PWA.
 * - On Android/desktop with native prompt: clicking "Installa" triggers it.
 * - On iOS Safari (no native prompt): shows manual instructions.
 * Hides automatically when the app is running in standalone mode or recently dismissed.
 */
export default function PwaInstallBanner() {
  const [show, setShow] = useState(false);
  const [hasNativePrompt, setHasNativePrompt] = useState(false);
  const [showIOSHelp, setShowIOSHelp] = useState(false);
  const [updateReady, setUpdateReady] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // already installed
    if (dismissedRecently()) return;

    const checkNative = () => setHasNativePrompt(isInstallAvailable());
    checkNative();

    const onInstallAvail = () => {
      setHasNativePrompt(true);
      setShow(true);
    };
    const onInstalled = () => {
      setShow(false);
      setHasNativePrompt(false);
    };
    const onUpdate = () => setUpdateReady(true);

    window.addEventListener("ccm-install-available", onInstallAvail);
    window.addEventListener("ccm-installed", onInstalled);
    window.addEventListener("ccm-update-available", onUpdate);

    // For iOS Safari, no native prompt fires — show after a short delay
    if (isIOS() && !hasNativePrompt) {
      const t = setTimeout(() => setShow(true), 4000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("ccm-install-available", onInstallAvail);
        window.removeEventListener("ccm-installed", onInstalled);
        window.removeEventListener("ccm-update-available", onUpdate);
      };
    }
    return () => {
      window.removeEventListener("ccm-install-available", onInstallAvail);
      window.removeEventListener("ccm-installed", onInstalled);
      window.removeEventListener("ccm-update-available", onUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInstall = async () => {
    if (hasNativePrompt) {
      const { outcome } = await promptInstall();
      if (outcome === "accepted") setShow(false);
    } else if (isIOS()) {
      setShowIOSHelp(true);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShow(false);
  };

  const handleUpdate = () => {
    promptUpdate();
    setUpdateReady(false);
  };

  if (updateReady) {
    return (
      <div
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 max-w-sm w-[calc(100%-2rem)] bg-slate-900 text-white rounded-2xl shadow-2xl p-4 flex items-center gap-3 animate-in slide-in-from-bottom"
        data-testid="pwa-update-banner"
      >
        <RefreshCw size={22} className="text-sky-400 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm">Nuova versione disponibile</div>
          <div className="text-xs text-slate-300">Tocca per aggiornare l'app</div>
        </div>
        <Button
          size="sm"
          onClick={handleUpdate}
          className="bg-sky-500 hover:bg-sky-600"
          data-testid="pwa-update-btn"
        >
          Aggiorna
        </Button>
      </div>
    );
  }

  if (!show) return null;

  return (
    <>
      <div
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 max-w-sm w-[calc(100%-2rem)] bg-white border border-slate-200 rounded-2xl shadow-xl p-4 flex items-center gap-3 animate-in slide-in-from-bottom"
        data-testid="pwa-install-banner"
      >
        <div className="h-11 w-11 rounded-xl bg-sky-50 flex items-center justify-center shrink-0">
          <Smartphone size={22} className="text-sky-500" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm">Installa Calcio Club</div>
          <div className="text-xs text-slate-500">
            Aggiungi alla schermata Home — funziona anche offline
          </div>
        </div>
        <Button
          size="sm"
          onClick={handleInstall}
          className="bg-sky-500 hover:bg-sky-600"
          data-testid="pwa-install-btn"
        >
          <Download size={14} className="mr-1" /> Installa
        </Button>
        <button
          onClick={handleDismiss}
          className="p-1 text-slate-400 hover:text-slate-700"
          aria-label="Chiudi"
        >
          <X size={16} />
        </button>
      </div>

      {showIOSHelp && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-4"
          onClick={() => setShowIOSHelp(false)}
        >
          <div
            className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="pwa-ios-help"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading text-lg font-bold">Installa su iPhone</h3>
              <button
                onClick={() => setShowIOSHelp(false)}
                className="text-slate-400 hover:text-slate-700"
              >
                <X size={18} />
              </button>
            </div>
            <ol className="space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <span className="h-7 w-7 rounded-full bg-sky-100 text-sky-700 font-bold inline-flex items-center justify-center shrink-0">
                  1
                </span>
                <div className="pt-0.5">
                  Tocca l'icona <Share size={14} className="inline mx-0.5 text-sky-500" /> <strong>Condividi</strong> nella barra di Safari.
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="h-7 w-7 rounded-full bg-sky-100 text-sky-700 font-bold inline-flex items-center justify-center shrink-0">
                  2
                </span>
                <div className="pt-0.5">
                  Scorri e seleziona <Plus size={14} className="inline mx-0.5 text-sky-500" /> <strong>Aggiungi alla schermata Home</strong>.
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="h-7 w-7 rounded-full bg-sky-100 text-sky-700 font-bold inline-flex items-center justify-center shrink-0">
                  3
                </span>
                <div className="pt-0.5">
                  Tocca <strong>Aggiungi</strong> in alto a destra. L'icona apparirà sulla home come una vera app.
                </div>
              </li>
            </ol>
            <Button
              className="w-full mt-5 bg-sky-500 hover:bg-sky-600"
              onClick={() => {
                setShowIOSHelp(false);
                handleDismiss();
              }}
            >
              Ho capito
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
